<template>
  <div class="ask-container">
    <!-- Linked contents -->
    <div class="results-container">
      <div class="results">
        <el-collapse accordion>
          <el-collapse-item
            v-for="message in queries"
            :key="message.queryId"
            class="accordion-item"
          >
            <template #title>
              <TextEllipsis
                :text="message.query.queryText"
                fontSize="14px"
              ></TextEllipsis>
            </template>
            <div>
              <div v-if="message.query">
                <div v-if="message.query.language">
                  <span class="text-m"
                    >Language : {{ message.query.language }}</span
                  >
                </div>
                <div v-if="message.query.keywords">
                  <span class="text-m">Keywords :</span>
                  <ul v-for="(keyword, j) in message.query.keywords" :key="j">
                    <li>{{ keyword }}</li>
                  </ul>
                </div>
                <div v-if="message.query.search && message.query.search.length">
                  <div
                    v-for="(document, j) in message.query.search"
                    :key="j"
                    class="search_item"
                  >
                    <div
                      class="search-result-button"
                      @click="handleSearchResultClick(message, document.id)"
                    >
                      <article-type-icon :item="document"></article-type-icon>
                      <TextEllipsis
                        :text="document.label"
                        fontSize="14px"
                      ></TextEllipsis>
                    </div>
                    <span class="score-text">Score : {{ document.score }}</span>
                    <details
                      v-if="
                        message.query.contexts &&
                        message.query.contexts[document.id]
                      "
                      class="score-text"
                    >
                      <summary>
                        <span>Context</span>
                      </summary>
                      <pre
                        class="context-str"
                        v-if="
                          message.query &&
                          message.query.contexts &&
                          message.query.contexts[document.id]
                        "
                        >{{ message.query.contexts[document.id] }}</pre
                      >
                    </details>
                  </div>
                </div>
              </div>
              <div v-else>No result</div>
              <button type="submit" @click="redoQuery(message.queryId)">
                <font-awesome-icon :icon="['fas', 'redo-alt']" />
                Replay
              </button>
            </div>
          </el-collapse-item>
        </el-collapse>
      </div>
    </div>
    <!-- Chat -->
    <div class="chat_container">
      <div class="messages_container" ref="message-container">
        <div v-if="!queries.length" class="message_empty">
          <h1>{{ $t('ask.title') }}<AiBadge /></h1>
          <img src="~assets/empty-states/empty-notification.svg" />
          <span>
            {{ $t('ask.description') }}
          </span>
        </div>
        <div v-for="(query, i) in queries" :key="i">
          <AskQuery
            :query="query.query"
            :markdown="query.markdown"
            :links="query.links"
            :citationLabels="query.citationLabels"
            :loading="query.loading"
            :error="query.error"
            :id="query.queryId"
            :feedback="query.feedback"
            :infos="query.infos"
            :dev-mode="true"
            @feedback="handleFeedback"
            @go-to-document="goToDocument"
          />
        </div>
      </div>
      <div class="input_container">
        <form @submit="sendMessage">
          <input
            v-model="message"
            placeholder="Ask mayday..."
            required
            value=""
            @keyup.stop="handleKeyUp"
          />
          <button type="submit">
            <font-awesome-icon :icon="['fal', 'paper-plane']" />
            Send
          </button>
        </form>
      </div>
    </div>
    <!-- Options -->
    <ask-parameters
      v-if="initialized"
      :optionSpecs="optionSpecs"
      :selectedOptions="selectedOptions"
    ></ask-parameters>
  </div>
</template>

<script>
import ArticleTypeIcon from 'components/SuperSearch/ArticleTypeIcon.vue';
import AskParameters from 'components/Ask/AskParameters.vue';
import TextEllipsis from 'components/TextEllipsis.vue';
import { v4 as uuid } from 'uuid';
import AskQuery from 'components/Ask/AskQuery.vue';
import { mapActions } from 'vuex';
import AiBadge from 'components/AI/AiBadge.vue';

function createObjectFromPaths(paths) {
  const obj = {};
  Object.entries(paths).forEach(([path, value]) => {
    if (value === null) {
      return;
    }
    const parts = path.split('.');
    let current = obj;
    for (let i = 0; i < parts.length - 1; i++) {
      const part = parts[i];
      if (!(part in current)) {
        current[part] = {};
      }
      current = current[part];
    }
    current[parts[parts.length - 1]] = value;
  });
  return obj;
}

export default {
  name: 'AskDev',
  components: {
    AiBadge,
    ArticleTypeIcon,
    AskQuery,
    AskParameters,
    TextEllipsis,
  },
  data: () => ({
    queries: [],
    loading: false,
    options: {},
    initializing: true,
    optionSpecs: [],
    selectedOptions: {},
    initialized: false,
    message: '',
    currentPreviousQuery: 0,
  }),
  async created() {
    await this.updateAskAccess(true);
    this.optionSpecs = (
      await this.$services.aiSearchClient.getParameters()
    ).data;
    this.selectedOptions = Object.fromEntries(
      this.optionSpecs.map((d) => [
        d.key,
        d.type === 'choice' ? d.values.find((d) => d.default).key : d.default,
      ]),
    );
    this.initialized = true;
    this.options = {};
  },
  methods: {
    handleFeedback(feedback, query, id) {
      this.sendAskFeedback({
        queryId: id,
        queryBody: query,
        feedback: feedback,
      });
      this.queries.find((d) => d.queryId === id).feedback = feedback.rating;
    },
    handleSearchResultClick(message, documentId) {
      const document = message.query.search.find((d) => d.id === documentId);
      this.goToDocument(document);
    },
    redoQuery(queryId) {
      const queryToRedo = this.queries.find((d) => d.queryId === queryId);
      const newQueryId = this.getCorrelationId();

      const currentMessageIndex = this.queries.length;
      this.queries.push({
        response: '',
        query: queryToRedo.query,
        createdAt: new Date(),
        loading: true,
        queryId: newQueryId,
        markdown: '',
        links: [],
        infos: {
          requestTime: Date.now(),
          firstChunkTime: null,
          lastChunkTime: null,
        },
        feedback: null,
        citationLabels: {},
      });

      this.fetchStream(queryId, currentMessageIndex);
    },
    sendMessage(event) {
      this.currentPreviousQuery = 0;
      event.preventDefault();

      const queryId = this.getCorrelationId();

      const currentMessageIndex = this.queries.length;
      this.queries.push({
        response: '',
        query: {
          queryText: this.message,
          ...this.options,
          ...createObjectFromPaths(this.selectedOptions),
        },
        createdAt: new Date(),
        loading: true,
        queryId: queryId,
        markdown: '',
        links: [],
        infos: {
          requestTime: Date.now(),
          firstChunkTime: null,
          lastChunkTime: null,
        },
        feedback: null,
        citationLabels: {},
      });

      this.fetchStream(queryId, currentMessageIndex);
      this.message = '';
      event.target.reset();
    },
    getCorrelationId() {
      return 'web-' + uuid();
    },
    async fetchStream(queryId, currentMessageIndex) {
      this.loading = true;
      const currentMessage = this.queries.at(currentMessageIndex);
      try {
        for await (const chunk of await this.fetchAsk(
          {
            queryId,
            queryBody: currentMessage.query,
            dev: true
          }
        )) {
          if (currentMessage.infos.firstChunkTime === null) {
            currentMessage.infos.firstChunkTime = Date.now();
          }
          this.parseResultChunk(chunk, currentMessageIndex);
        }
        this.loading = false;
        if (currentMessage.infos.lastChunkTime === null) {
          currentMessage.infos.lastChunkTime = Date.now();
        }
      } catch (e) {
        this.$message.error(e);
        this.loading = false;
        currentMessage.error = e.message;
      }
    },
    parseResultChunk(chunk, currentMessageIndex) {
      const currentMessage = this.queries.at(currentMessageIndex);
      const currentQueryInfo = currentMessage.query;
      console.log(chunk);
      if (chunk.type === 'answer') {
        currentMessage.response += chunk.content.text;

        for (let val of chunk.content.chunks) {
          if (!currentMessage.markdown) {
            currentMessage.markdown += val.text;
            continue;
          }
          if (val.link) {
            if (!currentMessage.citationLabels[val.link]) {
              currentMessage.citationLabels[val.link] =
                Object.keys(currentMessage.citationLabels).length + 1;
            }
            const citationLabel = currentMessage.citationLabels[val.link];
            currentMessage.markdown += `[${citationLabel}](${val.link})`;
          } else {
            currentMessage.markdown += val.text;
          }
        }
        currentMessage.loading = false;
      } else if (chunk.type === 'search') {
        currentQueryInfo.search = this.parseSearchResults(
          chunk.content.documents,
        );
      } else if (chunk.type === 'targeted-keywords') {
        currentQueryInfo.keywords = chunk.content;
      } else if (chunk.type === 'documents') {
        currentMessage.links = chunk.content;
      } else if (chunk.type === 'language') {
        currentQueryInfo.language = chunk.content;
      } else if (chunk.type === 'ask-contexts') {
        currentQueryInfo.contexts = chunk.content;
      } else if (chunk.type === 'error') {
        currentMessage.content = chunk.content;
        currentMessage.loading = false;
        currentMessage.error = {
          message: chunk.content,
        };
        throw new Error(chunk.content.message);
      } else {
        console.warn('Unsupported message type', chunk);
      }
    },
    getDocLabel(doc) {
      const navigationLanguage =
        this.$store.state.knowledgeModule.navigationLanguage;
      if (doc.translations[navigationLanguage]) {
        return doc.translations[navigationLanguage].label;
      } else {
        return doc.translations[doc.defaultLanguage].label;
      }
    },
    parseSearchResults(documents) {
      const linkedDocuments = [];
      if (documents) {
        documents.forEach((linkedDocument) => {
          if (!linkedDocument) return;
          linkedDocuments.push({
            score: linkedDocument.score,
            entity: linkedDocument.entity,
            label: this.getDocLabel(linkedDocument.doc),
            id: linkedDocument.doc.id,
            type: linkedDocument.doc.type,
            ancestors: linkedDocument.doc.ancestors,
            //breadcrumbs: this.parseBreadcrumbs(linkedDocument.doc),
          });
        });
      }
      return linkedDocuments;
    },
    goToDocument(doc) {
      const documentType = 'content'; //doc.type.toLowerCase();
      const path = `/knowledge/${documentType}/`;

      let subPath = doc.id;
      if (
        documentType === 'content' &&
        ['Step', 'keyStep'].includes(doc.type)
      ) {
        subPath = `${doc.ancestors[0].split('/')[0]}/step/${doc.id}`;
      }

      window.open(`${window.location.origin}${path}${subPath}`, '_blank');
    },
    handleKeyUp(event) {
      if (event.keyCode === 38) {
        event.preventDefault();
        this.currentPreviousQuery = Math.max(
          this.currentPreviousQuery - 1,
          -this.queries.length,
        );
        this.fillWithPreviousQuery();
      } else if (event.keyCode === 40) {
        event.preventDefault();
        this.currentPreviousQuery = Math.min(this.currentPreviousQuery + 1, 0);
        this.fillWithPreviousQuery();
      } else {
        this.currentPreviousQuery = 0;
      }
    },
    fillWithPreviousQuery() {
      if (
        this.currentPreviousQuery < 0 &&
        this.queries &&
        this.queries.at(this.currentPreviousQuery)
      ) {
        this.message = this.queries.at(
          this.currentPreviousQuery,
        ).query.queryText;
      }
    },
    ...mapActions('aiSearchModule', ['fetchAsk', 'sendAskFeedback']),
    ...mapActions(['updateAskAccess']),
  },
  computed: {
    defaultLanguage() {
      return this.$store.state.knowledgeModule.navigationLanguage;
    },
  },
  watch: {
    selectedOptions: {
      handler: async function (value) {
        const options = createObjectFromPaths(value);
        const result = await this.$services.aiSearchClient.validateParameters(
          options,
        );
        this.optionSpecs = result.data.parameters;
        Object.keys(value).forEach((key) => {
          if (result.data.updates[key] !== undefined) {
            value[key] = result.data.updates[key];
          }
        });
      },
      deep: true,
    },
    queries() {
      //smooth scroll to bottom of messages
      this.$nextTick(() => {
        this.$refs['message-container'].scrollTop =
          this.$refs['message-container'].scrollHeight;
      });
    },
  },
};
</script>

<style scoped lang="scss">
.ask-container {
  display: flex !important;
  height: 100vh;
  width: 100%;
}

.results-container {
  width: 20%;
  height: 100%;
  display: flex;
  flex-direction: column;

  .results {
    flex: 1;
    overflow-y: auto;
  }
}

.chat_container {
  display: flex !important;
  justify-content: space-between;
  flex-direction: column;
  height: 100%;
  width: 60%;
  margin: 0 auto;
  background-color: white;
  box-shadow: 0 0 10px 0 rgba(40, 40, 40, 0.1);
}

.messages_container {
  flex: 1;
  overflow-y: auto;
  padding: 10px;
  display: flex;
  gap: 16px;
  flex-direction: column;
}

.message_empty {
  height: 100%;
  display: flex;
  flex-direction: column;
  text-align: center;
  justify-content: center;
  align-items: center;
  width: 100%;
  gap: 16px;
  padding: 0 40px;

  h1 {
    display: flex;
  }

  span {
    white-space: pre-wrap;
  }
}

.message {
  padding: 10px;
  margin: 10px;
  border-radius: 5px;
  background-color: #f0f0f0;
  max-width: 60%;
  width: fit-content;

  &.question {
    align-self: flex-end;
    background-color: $blue-mayday;
    color: white;
    animation: question-enter 0.4s ease-in-out;
  }

  &.answer {
    align-self: flex-start;
    background-color: $grey-4-mayday;
    animation: answer-enter 0.4s ease-in-out;
  }

  &.error {
    align-self: flex-start;
    background-color: rgba($red-mayday, 0.75);
    animation: answer-enter 0.4s ease-in-out;
    color: white;
  }
}

.error_details {
  background-color: rgba($grey-4-mayday, 0.5);
  border-radius: 4px;
  padding: 8px;
  list-style: none;
  cursor: pointer;
}

.error_details summary {
  font-size: 12px;
  list-style: none;
  cursor: pointer;
}

.error_traceback {
  border-radius: 4px;
  font-size: 0.7em;
  padding: 12px;
  background-color: rgba($grey-4-mayday, 0.5);
  margin-top: 4px;
  margin-bottom: 0px;
}

.citation_list {
  background-color: rgba(white, 0.4);
  border-radius: 4px;
  padding: 8px;
  list-style: none;
  cursor: pointer;

  &:hover {
    background-color: rgba(white, 0.2);
  }
}

.citation_list summary {
  list-style: none;
  cursor: pointer;
}

.message_author {
  font-weight: bold;
  font-size: 0.7em;
  text-align: right;
}

.message_date {
  font-size: 0.5em;
  text-align: right;
}

.search-result-button {
  display: flex;
  align-items: center;
  color: $blue-mayday;
  font-size: 8px;
  border-radius: 4px;
  flex: none;
  padding: 4px;
  border: solid 1px $blue-mayday;
  transition: background-color 100ms ease-in-out;

  &:hover {
    background-color: white;
    color: $blue-5-mayday;
    cursor: pointer;
  }
}

button {
  padding: 4px 8px;
  border: 1px solid $purple-5-mayday;
  border-radius: 4px;
  background-color: $purple-5-mayday;
  color: white;

  &:hover {
    background-color: $purple-4-mayday;
  }
}

.search_item {
  padding: 6px 12px;
  margin-bottom: 6px;
  margin-right: 6px;
  border-radius: 4px;
  background-color: $grey-2-mayday;
}
.score-text {
  color: $grey-6-mayday;
  font-size: 10px;
}

.context-str {
  border: solid 1px $grey-6-mayday;
  border-radius: 4px;
  padding: 4px;
}

.input_container {
  padding: 10px;
  width: 100%;
  position: relative;
  input {
    width: 100%;
    padding: 10px 80px 10px 10px;
    border: 1px solid $grey-2-mayday;
    border-radius: 5px;
    margin-right: 10px;
  }
  button {
    position: absolute;
    right: 12px;
    top: 14px;
  }
}
.el-tag a {
  color: $grey-2-mayday;
}

@keyframes answer-enter {
  0% {
    transform: translateX(-100%);
    opacity: 0;
  }
  100% {
    transform: translateX(0);
    opacity: 1;
  }
}

@keyframes question-enter {
  0% {
    transform: translateX(100%);
    opacity: 0;
  }
  100% {
    transform: translateX(0);
    opacity: 1;
  }
}

.message_feedback {
  display: flex;
}

.accordion-item > * > * > * {
  padding-left: 10px;
}
.citation_small {
  outline: none;
  background-color: rgba(white, 0.5);
  display: inline-flex;
  justify-content: center;
  align-items: center;
  color: $blue-mayday;
  height: 16px;
  width: 16px;
  font-size: 8px;
  border-radius: 4px;
  flex: none;
  padding: 4px;
  border: solid 1px $blue-mayday;
  transition: background-color 100ms ease-in-out;

  &:hover {
    background-color: $grey-3-mayday !important;
  }
}

.citation {
  outline: none;
  background-color: rgba(white, 0.5);
  display: inline-flex;
  justify-content: center;
  align-items: center;
  color: $blue-mayday;
  height: 24px;
  width: 24px;
  font-size: 14px;
  border-radius: 4px;
  flex: none;
  padding: 4px;
  border: solid 1px $blue-mayday;
  transition: background-color 100ms ease-in-out;

  &:hover {
    background-color: $grey-3-mayday !important;
  }
}

.thumbs-icon {
  border: none;
  outline: none;
  background-color: transparent;
  display: flex;
  justify-content: center;
  align-items: center;
  color: $grey-8-mayday;
  height: 24px;
  width: 24px;
  font-size: 14px;
  border-radius: 4px;
  transition: background-color 100ms ease-in-out;

  &:hover {
    background-color: $grey-3-mayday !important;
  }
  &:active {
    background-color: $grey-4-mayday !important;
  }
}
</style>
